import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import HotelCard from "@/components/HotelCard";
import SEO from "@/components/SEO";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Filter, Search } from "lucide-react";

const STARS = [3, 4, 5];

export default function HotelsPage() {
  const [params, setParams] = useSearchParams();
  const [destinations, setDestinations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(params.get("search") || "");
  const [destination, setDestination] = useState(params.get("destination") || "all");
  const [brand, setBrand] = useState(params.get("brand") || "all");
  const [minStars, setMinStars] = useState(Number(params.get("min_stars") || 0));
  const [priceRange, setPriceRange] = useState([
    Number(params.get("min_price") || 0),
    Number(params.get("max_price") || 700),
  ]);
  const [sort, setSort] = useState(params.get("sort") || "editor");

  useEffect(() => {
    api.get("/destinations").then(({ data }) => setDestinations(data));
    api.get("/brands").then(({ data }) => setBrands(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (search) q.search = search;
    if (destination && destination !== "all") q.destination = destination;
    if (brand && brand !== "all") q.brand = brand;
    if (minStars) q.min_stars = minStars;
    q.min_price = priceRange[0];
    q.max_price = priceRange[1];
    q.sort = sort;
    api.get("/hotels", { params: q }).then(({ data }) => setHotels(data)).finally(() => setLoading(false));
    // Sync url
    const next = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) next.set(k, String(v));
    });
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, destination, brand, minStars, priceRange, sort]);

  const brandOptions = useMemo(() => [{ key: "all", name: "All operators" }, ...brands.map((b) => ({ key: b.name, name: b.name }))], [brands]);
  const activeDest = useMemo(() => destinations.find((d) => d.key === destination), [destinations, destination]);

  const clear = () => {
    setSearch(""); setDestination("all"); setBrand("all");
    setMinStars(0); setPriceRange([0, 1200]); setSort("editor");
  };

  return (
    <div>
      <SEO
        title={activeDest ? `Best hotels in ${activeDest.name}` : "Compare US hotels"}
        description="Filter across every major US hotel operator by destination, brand, star rating and price. Real rates from Booking.com, Hotels.com, Marriott, Hilton, Choice, IHG, Hyatt and Wyndham."
      />
      <section className="bg-charcoal text-sand relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl bg-terracotta/30 pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full blur-3xl bg-forest/40 pointer-events-none" />
        <div className="relative container-x py-14 md:py-20 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline text-terracotta-light">
              {activeDest ? `${activeDest.state} · USA` : "The comparator"}
            </div>
            <h1 className="mt-3 font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-balance">
              {activeDest ? `Best hotels in ${activeDest.name}` : "Best US hotels — every operator, ranked."}
            </h1>
            <p className="mt-5 text-sand/80 max-w-2xl text-lg leading-relaxed">
              Editorial rankings using a five-factor score (location, service, rooms, food, value). Compare live rates across Booking, Hotels.com, Marriott, Hilton and 12 more operators.
            </p>
          </div>
          <div className="md:col-span-4 grid grid-cols-3 gap-3">
            {[
              { v: hotels.length || "…", k: "Properties" },
              { v: brands.length || "…", k: "Operators" },
              { v: destinations.length || "…", k: "Cities" },
            ].map((s) => (
              <div key={s.k} className="border border-sand/15 p-3">
                <div className="overline text-sand/50">{s.k}</div>
                <div className="font-serif text-3xl mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-10 md:py-14">
        <div className="grid md:grid-cols-12 gap-10">
          {/* FILTERS */}
          <aside className="md:col-span-3">
            <div className="sticky top-28 space-y-8 border border-charcoal/10 bg-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 overline"><Filter size={12} /> Filters</div>
                <button onClick={clear} data-testid="filter-clear" className="text-xs text-terracotta hover:underline">Reset</button>
              </div>

              <div>
                <label className="overline block mb-3">Search</label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
                  <Input
                    data-testid="filter-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Hotel, city or brand"
                    className="pl-9 bg-sand/50 border-charcoal/10 focus-visible:ring-terracotta"
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-3">Destination</label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger data-testid="filter-destination" className="bg-sand/50 border-charcoal/10">
                    <SelectValue placeholder="All destinations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All destinations</SelectItem>
                    {destinations.map((d) => <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="overline block mb-3">Operator / brand</label>
                <Select value={brand} onValueChange={setBrand}>
                  <SelectTrigger data-testid="filter-brand" className="bg-sand/50 border-charcoal/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {brandOptions.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="overline block mb-3">Minimum stars</label>
                <div className="flex gap-2">
                  {[0, ...STARS].map((s) => (
                    <button
                      key={s}
                      data-testid={`filter-stars-${s}`}
                      onClick={() => setMinStars(s)}
                      className={`flex-1 py-2 text-xs border transition-colors ${
                        minStars === s
                          ? "bg-charcoal text-sand border-charcoal"
                          : "bg-white text-charcoal border-charcoal/15 hover:border-charcoal/40"
                      }`}
                    >
                      {s === 0 ? "Any" : `${s}+`}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline block mb-3">Price range (USD)</label>
                <div className="px-1">
                  <Slider
                    data-testid="filter-price"
                    min={0}
                    max={1200}
                    step={20}
                    value={priceRange}
                    onValueChange={setPriceRange}
                    className="py-3"
                  />
                </div>
                <div className="flex justify-between text-xs text-charcoal/70 mt-2 font-mono">
                  <span>${priceRange[0]}</span>
                  <span>${priceRange[1]}+</span>
                </div>
              </div>
            </div>
          </aside>

          {/* RESULTS */}
          <div className="md:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-charcoal/70" data-testid="results-count">
                {loading ? "Searching…" : `${hotels.length} propert${hotels.length === 1 ? "y" : "ies"}`}
              </div>
              <div className="flex items-center gap-3">
                <span className="overline">Sort</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="filter-sort" className="w-[180px] bg-white border-charcoal/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="editor">Editor's score</SelectItem>
                    <SelectItem value="price_asc">Price · low to high</SelectItem>
                    <SelectItem value="price_desc">Price · high to low</SelectItem>
                    <SelectItem value="stars">Stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-[260px] bg-stone/60 animate-pulse" />
                ))}
              </div>
            ) : hotels.length === 0 ? (
              <div className="border border-charcoal/10 bg-white p-12 text-center">
                <div className="overline mb-2">No matches</div>
                <div className="font-serif text-2xl">Try loosening the filters</div>
                <button onClick={clear} className="btn-ghost mt-6">Reset all</button>
              </div>
            ) : (
              <div className="space-y-4 md:space-y-5" data-testid="hotels-list">
                {hotels.map((h, i) => <HotelCard key={h.id} hotel={h} variant="list" rank={i + 1} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
